import { useCallback, useMemo, useState } from "react";
import { TimeSettings, useTimeSettings } from "@/state/time-settings";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogPage,
  MultiStepDialogContent,
} from "../paginated-dialog-content/multi-step-dialog-content";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  RunImmediatelyInput,
  ScheduleSpecificInput,
} from "./run-runImmediately-input";
import { TzPicker } from "@/routes/dashboard/-tz-picker";
import { FormDatePickerItem } from "../form-inputs/date-picker";
import { cn } from "@/lib/utils";
import { DeviceConfigDto, ScheduleJobScheduleDto } from "@/client";
import { useDropzone } from "react-dropzone";
import { PiFileCode } from "react-icons/pi";
import { Button } from "@/components/custom/button";
import { Input } from "@/components/ui/input";
import { TextArea } from "@/components/ui/textarea";
import { ConfigPreview } from "@/components/custom/config-deployment/config-preview";
import * as yaml from "js-yaml";
import { toast } from "sonner";

export const ScheduledConfigDeploymentSchema = z
  .object({
    file: z.custom<File>(
      (v) => v instanceof File || v === null || v === undefined,
      {
        message: "File is required",
      },
    ),
    config: z.custom<Partial<DeviceConfigDto>>(
      (v) => typeof v === "object" && v !== null,
      {
        message: "Valid configuration is required",
      },
    ),
    name: z.string().optional(),
    description: z.string().optional(),
    confirmRestart: z.boolean().refine((val) => val === true, {
      message: "You must confirm the restart",
    }),
    runImmediately: z.boolean(),
    schedule: z.date().optional(),
    timezone: z
      .string()
      .min(1, { message: "Please select a timezone" })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.schedule !== undefined;
    },
    {
      message: "Please select a schedule",
      path: ["schedule"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.timezone && data.timezone.length > 0;
    },
    {
      message: "Please select a timezone",
      path: ["timezone"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      if (!data.schedule) {
        return true;
      }
      return data.schedule > new Date();
    },
    {
      message:
        'Cannot schedule in the past. Use "Run Immediately" to deploy now.',
      path: ["schedule"],
    },
  );

interface ScheduledConfigDeploymentProps {
  apiCallback: (data: {
    deviceConfig: Partial<DeviceConfigDto>;
    name?: string;
    description?: string;
    schedule?: ScheduleJobScheduleDto;
  }) => Promise<any>;
  onClose?: () => void;
  count: number;
  initialConfig?: Partial<DeviceConfigDto>;
  initialName?: string;
  initialDescription?: string;
  deviceSelectionStep?: {
    title: string;
    content: React.ReactNode;
    fields?: string[];
    disableNext?: boolean;
    blocker?: () => Promise<boolean> | boolean;
  };
  onStepChange?: (step: number) => void;
}

export const ScheduledConfigDeployment = ({
  apiCallback,
  onClose,
  count,
  initialConfig,
  initialName,
  initialDescription,
  deviceSelectionStep,
  onStepChange,
}: ScheduledConfigDeploymentProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  // Notify parent of step changes
  useMemo(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const timeSettings = useTimeSettings();

  const form = useForm<z.infer<typeof ScheduledConfigDeploymentSchema>>({
    resolver: zodResolver(ScheduledConfigDeploymentSchema),
    defaultValues: {
      runImmediately: true, // Default to immediate
      timezone: timeSettings.timeZone,
      schedule: new Date(),
      confirmRestart: false,
      config: initialConfig,
      file: undefined,
      name: initialName || "",
      description: initialDescription || "",
    },
  });

  const runImmediately = form.watch("runImmediately");
  const timezone = form.watch("timezone");
  const schedule = form.watch("schedule");
  const config = form.watch("config");
  const file = form.watch("file");
  const confirmRestart = form.watch("confirmRestart");

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        // Validate file type
        if (!file.name.endsWith(".yaml") && !file.name.endsWith(".yml")) {
          toast.error("Please select a YAML file (.yaml or .yml)");
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const jsonObject = yaml.load(content) as Partial<DeviceConfigDto>;

            if (!jsonObject || typeof jsonObject !== "object") {
              toast.error(
                "Invalid YAML file. Content must be a valid configuration object.",
              );
              return;
            }

            form.setValue("file", file, { shouldValidate: true });
            form.setValue("config", jsonObject, { shouldValidate: true });
            toast.success("Configuration file uploaded successfully");
          } catch (error) {
            console.error("Error parsing YAML:", error);
            toast.error("Invalid YAML file. Please check the file format.");
          }
        };
        reader.readAsText(file);
      }
    },
    [form],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/yaml": [".yaml", ".yml"],
      "application/x-yaml": [".yaml", ".yml"],
    },
    maxFiles: 1,
  });

  const onSubmit = useCallback(
    async (data: z.infer<typeof ScheduledConfigDeploymentSchema>) => {
      const schedule: ScheduleJobScheduleDto | undefined = data.runImmediately
        ? undefined
        : {
            name: "Scheduled Config Deployment",
            startDate: data.schedule!,
            timezone: data.timezone!,
          };

      try {
        await apiCallback({
          deviceConfig: data.config,
          name: data.name,
          description: data.description,
          schedule,
        });
        onClose?.();
      } catch (error) {
        console.error("Failed to deploy config:", error);
      }
    },
    [apiCallback, onClose],
  );

  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <h1>{t("selectConfigFileToUpload")}</h1>
        <FormField
          control={form.control}
          name="file"
          render={() => (
            <FormItem>
              <FormControl>
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-4 min-h-[200px]",
                    isDragActive
                      ? "border-azure bg-azure/5"
                      : "border-muted-foreground/25",
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="rounded-full bg-muted p-4">
                    <PiFileCode className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    {file ? (
                      <>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("changeFile")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {t("dragAndDropYamlFile")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("orClickToBrowse")}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
    [t, file, getRootProps, getInputProps, isDragActive, form.control],
  );

  const Page2 = useMemo(() => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-1">
          <h4 className="text-sm font-medium text-muted-foreground">
            {t("deploymentInformation")}:
          </h4>
          <h4 className="text-sm font-medium text-muted-foreground">
            {t("configurationChangesToDeploy")}:
          </h4>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 text-sm">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel className="font-medium text-muted-foreground min-w-20">
                    {t("name")}:
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("enterDeploymentName")}
                      className="flex-1 px-2 py-1 h-8"
                    />
                  </FormControl>
                </div>
              </FormItem>
            )}
          />
          <div className="row-span-2">
            <div className="text-xs bg-muted p-2 rounded-md overflow-auto max-h-48 h-full">
              {config && <ConfigPreview config={config} />}
            </div>
          </div>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel className="font-medium text-muted-foreground min-w-20">
                    {t("description")}:
                  </FormLabel>
                  <FormControl>
                    <TextArea
                      {...field}
                      placeholder={t("enterDeploymentDescription")}
                      className="flex-1 min-h-[4rem] px-2 py-1"
                    />
                  </FormControl>
                </div>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="confirmRestart"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4 shadow-sm">
              <FormControl>
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="h-4 w-4"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm text-orange-600">
                  {count === 1
                    ? t("thisWillRestartTheSelectedDevice")
                    : t("thisWillRestartAllSelectedDevices")}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
    );
  }, [form.control, t, config]);

  const Page3 = useMemo(() => {
    return (
      <div className="space-y-4">
        <h1>{t("chooseWhenYouWantToDeploy")}</h1>
        <FormField
          control={form.control}
          name="runImmediately"
          render={({ field }) => (
            <>
              <div
                className={cn("border p-2 rounded-md mb-2", {
                  "border-azure bg-azure/10": field.value,
                })}
              >
                <FormItem className="">
                  <FormControl>
                    <RunImmediatelyInput
                      {...field}
                      description="Deploy the settings right away"
                    />
                  </FormControl>
                </FormItem>
              </div>

              <div
                className={cn("border p-2 rounded-md mb-2", {
                  "border-azure bg-azure/10": !field.value,
                })}
              >
                <FormItem className="">
                  <FormControl>
                    <ScheduleSpecificInput {...field} />
                  </FormControl>
                </FormItem>
                <div className={"flex  flex-col gap-2 ml-5 mt-4"}>
                  <FormDatePickerItem
                    {...timeSettings.as(timezone)}
                    name="schedule"
                    form={form}
                    label={t("date")}
                    button={{ disabled: field.value }}
                    container={{ className: field.value ? "opacity-50" : "" }}
                  />
                  <FormField
                    key={`timezone-${currentStep}`}
                    control={form.control}
                    name="timezone"
                    render={({ field: tzField }) => (
                      <FormItem className={field.value ? "opacity-50" : ""}>
                        <FormLabel>{t("timezone")}</FormLabel>
                        <FormControl>
                          <TzPicker
                            className="w-full"
                            value={tzField.value as TimeSettings["timezone"]}
                            onChange={(tz) => tzField.onChange(tz)}
                            isDisabled={field.value}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </>
          )}
        />
      </div>
    );
  }, [form, t, timezone, timeSettings, currentStep]);

  const Page4 = useMemo(() => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className={cn("border p-2 rounded-md mb-2 bg-azure/5")}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="">{t("fileName")}</div>
                <div className="">{t("devicesCount")}</div>
              </div>
              <div className="space-y-2">
                <div className="font-normal">
                  {file?.name || (initialConfig ? t("configuration") : "")}
                </div>
                <div className="font-normal">{count}</div>
              </div>
            </div>
          </div>
          <div className={cn("border p-2 rounded-md mb-2 bg-azure/5")}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="">{t("deploymentType")}</div>
                {!runImmediately && (
                  <>
                    <div className="">{t("scheduledDate")}</div>
                    <div className="">{t("scheduledTime")}</div>
                    <div className="">{t("timezone")}</div>
                  </>
                )}
              </div>
              <div className="space-y-2">
                <div className="font-normal">
                  {runImmediately ? t("immediate") : t("scheduled")}
                </div>
                {!runImmediately && (
                  <>
                    <div className="font-normal">
                      {schedule?.toLocaleDateString()}
                    </div>
                    <div className="font-normal">
                      {schedule?.toLocaleTimeString()}
                    </div>
                    <div className="font-normal">{timezone}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [t, runImmediately, timezone, schedule, file, count]);

  const pages: DialogPage[] = useMemo(
    () =>
      [
        !initialConfig && {
          title: t("uploadConfig"),
          content: Page1,
          fields: ["file", "config"],
          disableNext: !file,
        },
        {
          title: t("configuration"),
          content: Page2,
          fields: ["name", "description", "confirmRestart"],
          disableNext: !confirmRestart,
        },
        deviceSelectionStep && {
          title: deviceSelectionStep.title,
          content: deviceSelectionStep.content,
          fields: deviceSelectionStep.fields,
          disableNext: deviceSelectionStep.disableNext,
          blocker: deviceSelectionStep.blocker,
        },
        {
          title: t("scheduler"),
          content: Page3,
          fields: runImmediately ? [] : ["schedule", "timezone"],
        },
        {
          title: t("summary"),
          content: Page4,
        },
      ].filter(Boolean) as DialogPage[],
    [
      Page1,
      Page2,
      Page3,
      Page4,
      t,
      runImmediately,
      confirmRestart,
      file,
      initialConfig,
      deviceSelectionStep,
    ],
  );

  const handleNext = useCallback(async () => {
    const fields = pages[currentStep].fields;

    // Validate without focusing
    const isStepValid = await form.trigger(
      fields as (keyof z.infer<typeof ScheduledConfigDeploymentSchema>)[],
      { shouldFocus: false },
    );

    if (isStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, pages, form]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col flex-1 min-h-0"
      >
        <MultiStepDialogContent
          pages={pages}
          currentStep={currentStep}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={form.handleSubmit(onSubmit)}
          finishButtonText={t("deploy")}
          className="w-full flex-1 min-h-0"
        />
      </form>
    </Form>
  );
};
